/**
 * Finance Page
 * Display all financial transactions and summary
 * Keep the query flow and UI aligned with the Mission page
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useDebounce } from '../../hooks';
import { Box, Typography, SelectChangeEvent } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState, TablePagination } from '../../components/common';
import { Card, CardContent } from '../../components/ui';
import { FinanceService } from '../../services/finance/FinanceService';
import { FinancialSummaryCard } from './components/FinancialSummaryCard';
import { TransactionFilters } from './components/TransactionFilters';
import { TransactionTable } from './components/TransactionTable';
import type {
  FinancialTransaction,
  FinancialSummary,
  FinancialFilter,
  PaymentStatus,
} from '../../types/finance';

interface DateRange {
  start: string;
  end: string;
}

const FinancePage: React.FC = () => {
  const theme = useTheme();
  const [transactions, setTransactions] = useState<FinancialTransaction[]>([]);
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<PaymentStatus>>(new Set());
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);

  const selectedStatusesKey = useMemo(() => Array.from(selectedStatuses).sort().join(','), [selectedStatuses]);
  const dateRangeKey = useMemo(() => (dateRange ? `${dateRange.start || ''}|${dateRange.end || ''}` : ''), [dateRange]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const start = dateRange?.start;
        const end = dateRange?.end;
        const filter: FinancialFilter = {
          search: debouncedSearchValue || undefined,
          dateRange: start || end
            ? {
              start: start ? new Date(start) : (end ? new Date(end) : new Date()),
              end: end ? new Date(end + 'T23:59:59') : (start ? new Date(start + 'T23:59:59') : new Date()),
            }
            : undefined,
          status: selectedStatuses.size > 0 ? Array.from(selectedStatuses) : 'all',
        };
        const hasStatusFilter = selectedStatuses.size > 0;
        if (hasStatusFilter) {
          const transactionsResult = await FinanceService.getTransactions(page, pageSize, filter);
          if (!cancelled) {
            setTransactions(transactionsResult.data);
            setTotalResults(transactionsResult.total);
            setSummary(null);
          }
        } else {
          const [transactionsResult, summaryResult] = await Promise.all([
            FinanceService.getTransactions(page, pageSize, filter),
            FinanceService.getFinancialSummary(filter),
          ]);
          if (!cancelled) {
            setTransactions(transactionsResult.data);
            setTotalResults(transactionsResult.total);
            setSummary(summaryResult);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch financial data:', error);
          setTransactions([]);
          setSummary(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, debouncedSearchValue, dateRangeKey, selectedStatusesKey]);

  const handleStatusToggle = (status: PaymentStatus) => {
    setSelectedStatuses((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(status)) {
        newSelected.delete(status);
      } else {
        newSelected.add(status);
      }
      return newSelected;
    });
    setPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(1);
  };

  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    const newRange = {
      ...(dateRange || { start: '', end: '' }),
      [field]: value,
    };
    setDateRange(newRange.start || newRange.end ? newRange : null);
    setPage(1);
  };

  const handleClearDateRange = () => {
    setDateRange(null);
    setPage(1);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  return (
    <MainLayout>


      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: theme.typography.h4.fontWeight,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
          }}
        >
          Finance
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
          View all financial transactions, booking fees, and platform fees.
        </Typography>
      </Box>

      <TransactionFilters
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        onClearDateRange={handleClearDateRange}
        selectedStatuses={selectedStatuses}
        onStatusToggle={handleStatusToggle}
      />

      {isLoading ? (
        <Box sx={{ padding: theme.spacing(6), display: 'flex', justifyContent: 'center' }}>
          <LoadingSpinner />
        </Box>
      ) : (
        <>
          {summary && <FinancialSummaryCard summary={summary} />}

          <Card>
            <CardContent noPadding>
              {transactions.length === 0 ? (
                <Box sx={{ padding: theme.spacing(4) }}>
                  <EmptyState
                    title="No Transactions Found"
                    message="There are no transactions matching your criteria."
                  />
                </Box>
              ) : (
                <>
                  <Box sx={{ padding: theme.spacing(3) }}>
                    <TransactionTable transactions={transactions} />
                  </Box>
                  <TablePagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalResults}
                    onPageChange={setPage}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </MainLayout>
  );
};

export default FinancePage;
